// 必须在所有其他导入之前加载 polyfill，确保兼容性
import '@/utils/polyfills';

// 发版后旧版本 chunk 文件 404 时，静默刷新页面让用户平滑升级到最新版本
// 防刷死机制：60s 内同一 chunk 不重复触发刷新
const _reloadedChunks = new Set<string>();
window.addEventListener('vite:preloadError', (event: Event) => {
  const url = (event as CustomEvent<{ id: string }>).detail?.id ?? 'unknown';
  if (_reloadedChunks.has(url)) return; // 同一 chunk 已刷新过，不再重复
  _reloadedChunks.add(url);
  setTimeout(() => _reloadedChunks.delete(url), 60_000); // 60s 后允许再次刷新
  window.location.reload();
});
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { ConfigProvider, Spin, App as AntdApp } from 'antd';
import { PersistGate } from 'redux-persist/integration/react';
import store, { persistor } from './store';
import App from './App.tsx';
import './assets/styles/global.css';
import { StyleProvider } from '@ant-design/cssinjs';
import { MessageProvider } from '@/components/common/Message';
import { intelligentPrewarm } from '@/utils/routePrewarmer';
import { preloadCache } from '@/utils/cache/cachedRequest';
import GlobalScrollbarStyles from '@/styles/GlobalScrollbarStyles';
import { initAPM, apmClient, getSessionId, isAPMEnabled } from '@/config/apm';
import { initAppVh } from '@/utils/viewportHeight';
import { bootstrapLocalStorage } from '@/utils/storageQuota';
import { syncRouteContext } from '@/observability/context';
import { startCounterFlush } from '@/observability/counter';
import { startCrashWindowSnapshot, reportPreviousCrashWindow } from '@/observability/window';
import { router } from '@/router';
// Clarity 异步加载 - 移除同步导入以减少主线程阻塞
// import Clarity from '@microsoft/clarity';
import { TaskProgressProvider } from '@/contexts/TaskProgressContext';
import { VersionProvider } from '@/contexts/VersionContext';
import { GPUMemoryMonitor } from '@/utils/gpuMemoryMonitor';
import { BrandProvider } from '@/contexts/BrandContext';
// i18n 多语言
import '@/i18n/config';
import i18n from '@/i18n/config';
import { I18nextProvider } from 'react-i18next';
import { LanguageProvider } from '@/contexts/LanguageContext';
import AntdLocaleProvider from '@/components/common/AntdLocaleProvider';
import { CampaignStatusProvider } from '@/contexts/CampaignStatusContext';

// localStorage 配额自愈：老用户机器可能早已写满（历史遗留），启动时先把旧画布快照
// 收敛掉，避免 persist / 登录态写入失败、以及跨标签写锁静默失效（见 utils/storageQuota）
bootstrapLocalStorage();

// 初始化 APM 监控（内部按环境闸门判断，非 prod 不会 init）
initAPM();

// 以下均为「依赖 APM 才有意义」的监控：闸门关闭时 APM 未 init，上报全部空转，
// 而 GPU/内存监控仍会持续定时采样（徒耗性能且数据无处可去），故一并按闸门启停。
if (isAPMEnabled()) {
  // pid / feature 路由维度归一化（P0-13）：画布等动态路由统一收敛为路由模板，
  // 否则每个 canvasId 都会生成一个独立 pid，页面维度基数无限膨胀
  syncRouteContext(window.location.pathname);
  router.subscribe((state) => syncRouteContext(state.location.pathname));

  // 聚合计数器（P0-14）：为「失败率」类告警提供分母，每分钟一条聚合上报
  startCounterFlush();

  // 崩溃前后环形缓冲（P1-6）：只写内存，仅错误时随 dumpErrorDetail 携带；定期快照，
  // 若上次非正常退出则在启动时补报崩溃前现场
  startCrashWindowSnapshot();
  reportPreviousCrashWindow();

  // 启动 GPU / 内存全局监控（必须在 initAPM 之后调用）
  GPUMemoryMonitor.getInstance().start();
}

// 初始化视口高度自定义属性（解决移动端和平板设备中 100vh 不适应屏幕大小的问题）
initAppVh();

/**
 * 全局错误处理（P1-8）
 *
 * ⚠️ 原实现会把**任何**未捕获错误都 clearDataAndRedirectToLogin()（清数据 + 踢下线），
 *    一个无关紧要的 JS 报错就把用户登出 —— 该有害行为已移除。
 *
 * 现只记录日志，**明确不做**两件事：
 *  1. 不踢下线：认证失效（401）由 src/utils/request.ts 单独处理；
 *  2. 不上报 APM：火山 SDK 的 jsError 插件（captureGlobalAsync: true）已自动捕获
 *     window error / unhandledrejection，此处再报一次只会重复占配额、不增加信息量。
 */
const handleGlobalError = (error: unknown, context: string = ''): void => {
  try {
    console.error(`[Global Error Handler] ${context}捕获到错误:`, error);
  } catch {
    // 连日志打印都失败也绝不能影响页面运行
  }
};

// 全局错误处理：捕获未处理的错误与 Promise 拒绝，仅记录日志（不影响用户会话）
const setupGlobalErrorHandler = () => {
  // 捕获未处理的 JavaScript 错误（处理所有错误，不仅仅是特定类型）
  window.addEventListener('error', (event) => {
    // 忽略资源加载错误（如图片、脚本等加载失败）
    if (event.target && (event.target as HTMLElement).tagName) {
      return;
    }

    const error = event.error || new Error(event.message || 'Unknown error');
    handleGlobalError(error, '未处理的 JavaScript 错误');
  });

  // 捕获未处理的 Promise 拒绝（处理所有错误）
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason || 'Unknown promise rejection'));
    handleGlobalError(error, '未处理的 Promise 拒绝');
  });
};

// 设置全局错误处理（P1-8 启用：只记录日志，不踢下线、不重复上报）
setupGlobalErrorHandler();

// 异步延迟加载 Clarity - 减少主线程阻塞，提升 TBT 指标
if (import.meta.env.MODE === 'prod') {
  // 改为「等真正空闲」而非固定等 3 秒：原实现下，3 秒内的首屏崩溃/白屏完全录不到，
  // 而那恰恰是最需要回放的场景。空闲时机会比固定 3s 早得多。
  const CLARITY_LOAD_TIMEOUT_MS = 3000;

  let clarityLoadStarted = false;
  const loadClarity = () => {
    if (clarityLoadStarted) return; // 幂等：Clarity.init 重复调用会污染会话与 tag
    clarityLoadStarted = true;
    import('@microsoft/clarity').then(({ default: Clarity }) => {
      const projectId = "u8bpeuse1b";
      Clarity.init(projectId);
      // 打通 APM ↔ Clarity：APM 异常发现问题后，按 sessionId 到 Clarity 检索同一会话的回放。
      // 复用 getSessionId() 而非在此另读一次 config()——二者真值源现已统一为 SDK 内置
      // sessionId，这样保证「Clarity tag / 自定义事件 / 官方异常」三处是同一个值。
      const apmSessionId = getSessionId();
      const apmUserId = (apmClient.config() as { userId?: string } | undefined)?.userId;
      Clarity.identify(apmUserId ?? '');
      Clarity.setTag('apm_session', apmSessionId);
      Clarity.setTag('release', import.meta.env.VITE_APM_RELEASE ?? '');
      console.log('✅ Clarity 分析已加载');
    }).catch(err => {
      console.warn('⚠️ Clarity 加载失败:', err);
    });
  };

  if ('requestIdleCallback' in window) {
    // timeout 选项不可省：主线程持续繁忙时可能永远等不到空闲，加上后最迟 3s 强制执行，
    // 保证不会比原来的固定 3s 更差，只可能更早。
    requestIdleCallback(loadClarity, { timeout: CLARITY_LOAD_TIMEOUT_MS });
  } else {
    // Safari 全系不支持 requestIdleCallback（caniuse：13.1+ 仍为默认禁用），
    // 此兜底是必需而非可选——否则 Safari 用户将永远加载不到 Clarity。
    setTimeout(loadClarity, CLARITY_LOAD_TIMEOUT_MS);
  }
}

// 启动应用
const rootElement = document.getElementById('root');
if (rootElement) {
  ReactDOM.createRoot(rootElement).render(
    // <React.StrictMode>
      <Provider store={store}>
        <PersistGate
          loading={
            <div>
              <Spin size="large">
                <div style={{ marginTop: 16 }}>加载存储数据...</div>
              </Spin>
            </div>
          }
          persistor={persistor}
        >
          <I18nextProvider i18n={i18n}>
            <BrandProvider>
              <LanguageProvider>
                <AntdLocaleProvider>
                <AntdApp>
                  <MessageProvider>
                    <TaskProgressProvider>
                      <VersionProvider>
                        <CampaignStatusProvider>
                          <StyleProvider hashPriority="high">
                            <GlobalScrollbarStyles />
                            <Suspense
                              fallback={
                                <div>
                                  <Spin size="large" />
                                </div>
                              }
                            >
                              <App />
                            </Suspense>
                          </StyleProvider>
                        </CampaignStatusProvider>
                      </VersionProvider>
                    </TaskProgressProvider>
                  </MessageProvider>
                </AntdApp>
              </AntdLocaleProvider>
            </LanguageProvider>
          </BrandProvider>
          </I18nextProvider>
        </PersistGate>
      </Provider>
  );

  // 暂时禁用智能路由预热，避免 Minified React error #306
  // intelligentPrewarm();

  // 预加载关键数据
  const preloadEssentialData = async () => {
    try {
      // 预加载模型列表，确保在用户进入生成页面前就有数据
      await Promise.all([
        preloadCache('/model/list', { type_tags: ['image'] }, 30 * 60 * 1000),
        preloadCache('/model/list', { type_tags: ['video'] }, 30 * 60 * 1000),
        preloadCache('/model/list', { type_tags: ['text'] }, 30 * 60 * 1000),
      ]);
      console.log('✅ 关键数据预加载完成');
    } catch (error) {
      console.warn('⚠️ 关键数据预加载失败:', error);
    }
  };

  // 延迟预加载，避免影响应用启动
  setTimeout(preloadEssentialData, 1000);

  // 为开发工具预留的扩展点（如需使用）
  if (import.meta.env.MODE === 'development') {
    // 开发环境特定代码可以在这里添加
    console.log('🚀 AMAI 开发环境已启动');
  }
}
